// Topbar conversion widget: a chunky progress bar with "Converting X/Y", expandable
// dropdown listing every job in the batch (status, progress, cancel for queued ones).
import { api, type Job } from "../api";

const ACTIVE = new Set(["queued", "running"]);

export class JobsWidget {
  private root: HTMLElement;
  private bar: HTMLElement;
  private fill: HTMLElement;
  private text: HTMLElement;
  private panel: HTMLElement;
  private timer: number | null = null;
  private projectId: number | null = null;
  private watchIds = new Set<number>();
  private lastFinished = -1;
  /** Called whenever a job in the watched batch finishes (assets landed). */
  onAssetsLanded: () => void = () => {};

  constructor(mount: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "jobs-widget";
    this.root.innerHTML = `
      <button class="jobs-bar" title="Conversion progress">
        <span class="jobs-fill"></span>
        <span class="jobs-text"></span>
        <span class="jobs-caret">▾</span>
      </button>
      <div class="jobs-panel"></div>`;
    this.bar = this.root.querySelector(".jobs-bar")!;
    this.fill = this.root.querySelector(".jobs-fill")!;
    this.text = this.root.querySelector(".jobs-text")!;
    this.panel = this.root.querySelector(".jobs-panel")!;
    this.bar.addEventListener("click", () => this.root.classList.toggle("open"));
    document.addEventListener("click", (e) => {
      if (!this.root.contains(e.target as Node)) this.root.classList.remove("open");
    });
    mount.append(this.root);
  }

  /** Start (or extend) watching a batch of job ids for a project. */
  watch(projectId: number, jobIds: number[]): void {
    this.projectId = projectId;
    jobIds.forEach((id) => this.watchIds.add(id));
    this.root.classList.add("show");
    if (!this.timer) {
      this.timer = window.setInterval(() => void this.poll(), 1200);
      void this.poll();
    }
  }

  private async poll(): Promise<void> {
    if (this.projectId == null) return;
    const { jobs } = await api.listJobs(this.projectId);
    const batch = jobs.filter((j) => this.watchIds.has(j.id));
    if (!batch.length) return this.stop();

    const done = batch.filter((j) => j.status === "succeeded").length;
    const failed = batch.filter((j) => j.status === "failed").length;
    const cancelled = batch.filter((j) => j.status === "cancelled").length;
    const finished = done + failed + cancelled;
    const total = batch.length;

    this.fill.style.width = `${Math.max(4, Math.round((finished / total) * 100))}%`;
    this.fill.classList.toggle("has-fail", failed > 0);
    const extra = failed ? ` · ${failed} failed` : "";
    this.text.textContent =
      finished < total ? `Converting ${finished}/${total}${extra}` : `Done ${done}/${total}${extra}`;
    this.renderPanel(batch);

    if (finished !== this.lastFinished) {
      this.lastFinished = finished;
      this.onAssetsLanded();
    }
    if (!batch.some((j) => ACTIVE.has(j.status))) {
      // Batch complete: stop polling, keep the widget visible for a while.
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
      window.setTimeout(() => {
        if (!this.timer) {
          this.root.classList.remove("show", "open");
          this.watchIds.clear();
          this.lastFinished = -1;
        }
      }, 6000);
    }
  }

  private stop(): void {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
    this.root.classList.remove("show", "open");
  }

  private renderPanel(batch: Job[]): void {
    this.panel.innerHTML = "";
    for (const j of [...batch].sort((a, b) => a.id - b.id)) {
      const row = document.createElement("div");
      row.className = "jobs-row";
      const cls =
        j.status === "succeeded" ? "ok" :
        j.status === "failed" ? "fail" :
        j.status === "cancelled" ? "muted" :
        j.status === "running" ? "run" : "queue";
      row.innerHTML = `
        <span class="jobs-status ${cls}">${j.status === "running" ? "●" : j.status === "succeeded" ? "✓" : j.status === "failed" ? "✗" : j.status === "cancelled" ? "-" : "…"}</span>
        <span class="jobs-label" title="${j.message ?? ""}">${j.label ?? `job ${j.id}`}</span>
        <span class="jobs-state">${j.status}${j.status === "running" ? ` ${Math.round(j.progress)}%` : ""}</span>`;
      if (j.status === "queued") {
        const cancel = document.createElement("button");
        cancel.className = "jobs-cancel";
        cancel.textContent = "✕";
        cancel.title = "Cancel this conversion";
        cancel.addEventListener("click", async (e) => {
          e.stopPropagation();
          if (this.projectId != null) {
            await api.cancelJob(this.projectId, j.id);
            void this.poll();
          }
        });
        row.append(cancel);
      }
      this.panel.append(row);
    }
  }
}
